import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { map } from 'rxjs/operators';

/**
 * Permissões das ações de gravação da Gestão de Derivativos v2 (card 3902).
 *
 * Cada flag usa a MESMA claim que o endpoint chamado confere no backend
 * (`Controllers/V1/DerivativeCalculator/...`). Sem a claim a ação some da tela; o grupo
 * "Controle - Somente leitura" navega pela demanda sem nenhuma ação que grave.
 *
 * Root e só leitura de store: pode ser injetado na página, nos cards, na tabela e nas modais.
 */
@Injectable({ providedIn: 'root' })
export class DerivativeActionPermissionsService {
  private readonly permissionsService = inject(PermissionsService);

  /**
   * `DerivativeCalculator.UPDATE`: salvar a demanda (`PUT derivatives/demands/{id}`), e por isso também
   * "Adicionar simulação", "Duplicar" e "Recalcular" que só valem depois do Salvar; grupos
   * (`derivative-groups/demand/...`), vincular a outra dívida (`PUT operations/ndf/{id}/loan-link`),
   * vínculo automático de parcelas e o recálculo futuro do MtM (`mtm-daily/update-future-all`).
   */
  readonly canUpdate = this.flag(Permissions.DerivativeCalculator.UPDATE);
  /** `DerivativeCalculator.CREATE_DEMAND`: criar demanda e importar operações (`POST derivatives/demands[/import]`). */
  readonly canCreateDemand = this.flag(Permissions.DerivativeCalculator.CREATE_DEMAND);
  /** `DerivativeCalculator.CREATE`: gerar o MtM diário (`POST mtm-daily/{id}/generate`, `generate-all`) e criar por IA. */
  readonly canCreate = this.flag(Permissions.DerivativeCalculator.CREATE);
  /** `DerivativeCalculator.UPDATE_OPERATION_STATUS`: mudar o status (`PUT operations/ndf/operation-status-*`). */
  readonly canUpdateOperationStatus = this.flag(
    Permissions.DerivativeCalculator.UPDATE_OPERATION_STATUS
  );
  /** `DerivativeCalculator.UPDATE_LIQUIDATE`: liquidar e reverter (`PUT operations/ndf/liquidate|revert-liquidate`). */
  readonly canLiquidate = this.flag(Permissions.DerivativeCalculator.UPDATE_LIQUIDATE);
  /** `DerivativeCalculator.EARLY_REVERSAL`: reversão antecipada (`PUT operations/ndf/liquidate-early-reversal`). */
  readonly canEarlyReversal = this.flag(Permissions.DerivativeCalculator.EARLY_REVERSAL);
  /** `DerivativeCalculator.DELETE_OPERATION`: excluir operação (`DELETE operations/ndf/{id}`). */
  readonly canDeleteOperation = this.flag(Permissions.DerivativeCalculator.DELETE_OPERATION);
  /** `DerivativeCalculator.ATTACHMENT`: enviar e apagar anexo (`POST/DELETE operations/ndf/operation/.../attachment`). */
  readonly canManageAttachments = this.flag(Permissions.DerivativeCalculator.ATTACHMENT);
  /** `Rfq.CREATE`: "Criar RFQ" a partir da simulação. */
  readonly canCreateRfq = this.flag(Permissions.Rfq.CREATE);
  /** `RiskCommittee.CREATE`: "Enviar ao Comitê". */
  readonly canCreateCommittee = this.flag(Permissions.RiskCommittee.CREATE);

  private flag(permission: string): Signal<boolean> {
    return toSignal(this.permissionsService.hasPermission(permission).pipe(map(Boolean)), {
      initialValue: false,
    });
  }
}

/** Nome de cada flag do serviço (`canUpdate`, `canLiquidate`...), para quem lê a flag por nome. */
export type DerivativeActionPermissionFlag = {
  [K in keyof DerivativeActionPermissionsService]: DerivativeActionPermissionsService[K] extends Signal<boolean>
    ? K
    : never;
}[keyof DerivativeActionPermissionsService];
